/**
 * Client-side mirror of the backend `seasonDivisionService` (pure, no DB).
 *
 * A reusable, season-agnostic membership stores its split definitions as
 * date-independent **fractions** in `divisionScheme`. Concrete dates ALWAYS come
 * from a season term at resolve time. The authoritative resolution happens on
 * the backend (Prague wall-clock); this module reproduces the same segment math
 * so the admin editor and the member buy flow can render a live preview of the
 * concrete date windows + prices WITHOUT a round-trip. Dates here are for
 * DISPLAY ONLY — the backend re-resolves on enrollment.
 *
 * Segment math (matches backend seasonDivisionService.ts):
 *   span = inclusive day-count of [termStart, termEnd]
 *   a split fraction f maps to day round(f * span)
 *   bounds = [0, round(s1*span), …, round(sN*span), span]
 *   segment i occupies day offsets [bounds[i], bounds[i+1]) — first segment
 *   starts at termStart (offset 0), last ends at termEnd (offset span-1).
 */

export type DivisionSegmentKind = 'halves' | 'thirds' | 'quarters' | 'custom';
export type ChosenSchemeKind = 'full' | DivisionSegmentKind;

/** One division lane. `splits` ascending in (0,1) length N; `prices` length N+1. */
export interface DivisionSegmentScheme {
  kind: DivisionSegmentKind;
  splits: number[];
  prices: number[];
}

/** The full division definition stored on `memberships.divisionScheme`. */
export interface DivisionScheme {
  fullPrice: number;
  premium: number; // 0..0.25 informational suggestion factor (UI only)
  schemes: DivisionSegmentScheme[];
}

/** A concrete priced segment resolved against a season term (display). */
export interface ResolvedSegment {
  scheme: ChosenSchemeKind;
  index: number | null; // null for 'full'
  price: number;
  startOffset: number; // day offset from term start
  endOffset: number; // inclusive day offset from term start
  days: number; // inclusive day-count
  startDate: Date | null; // null when no term provided
  endDate: Date | null;
}

const DAY = 86_400_000;

/** Preset even splits for a scheme kind (custom seeds a single mid split). */
export function presetSplits(kind: DivisionSegmentKind): number[] {
  switch (kind) {
    case 'halves':
      return [0.5];
    case 'thirds':
      return [1 / 3, 2 / 3];
    case 'quarters':
      return [0.25, 0.5, 0.75];
    default:
      return [0.5];
  }
}

/** Number of segments a scheme yields (splits.length + 1). */
export function segmentCount(scheme: DivisionSegmentScheme): number {
  return scheme.splits.length + 1;
}

/** The whole-season fraction covered by segment `i` of a scheme. */
export function segmentFraction(scheme: DivisionSegmentScheme, index: number): number {
  const bounds = [0, ...scheme.splits, 1];
  const lo = bounds[index] ?? 0;
  const hi = bounds[index + 1] ?? 1;
  return Math.max(0, hi - lo);
}

/**
 * Suggested per-segment price = fullPrice × fraction × (1 + premium), rounded UP
 * to the nearest 10. Used to pre-fill new lanes.
 *
 * Rounding must go up, not to nearest: with `Math.round` a full price of 10000
 * split into thirds suggested 3330 each (10000/3 = 3333.33 → 3330), so buying
 * the three segments cost 9990 — CHEAPER than the whole season. That inverts the
 * `premium` factor, which exists to make splitting cost the same or more. Up-
 * rounding gives 3340 (sum 10020), so a split is never the cheaper route.
 *
 * Exact fractions are unaffected: halves of 10000 stay 5000, quarters stay 2500.
 * This only pre-fills the editor — prices already stored keep their values until
 * an admin re-saves the lane.
 */
export function suggestedPrice(scheme: DivisionScheme, fraction: number): number {
  const raw = scheme.fullPrice * fraction * (1 + (scheme.premium || 0));
  return Math.ceil(raw / 10) * 10;
}

/** Parse a Drizzle `date` ('YYYY-MM-DD') or a Date into a UTC-anchored Date. */
function parseTerm(value: string | Date | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

/** Inclusive day span between two term dates, or null when unresolvable. */
export function termDaySpan(
  termStart: string | Date | null | undefined,
  termEnd: string | Date | null | undefined,
): number | null {
  const start = parseTerm(termStart);
  const end = parseTerm(termEnd);
  if (!start || !end) return null;
  const span = Math.round((end.getTime() - start.getTime()) / DAY) + 1;
  return span >= 1 ? span : null;
}

/** Build cut offsets in day units for a lane: [0, round(s*span), …, span]. */
function cutOffsets(splits: number[], span: number): number[] {
  return [0, ...splits.map((f) => Math.round(f * span)), span];
}

function addDays(base: Date | null, offset: number): Date | null {
  if (!base) return null;
  return new Date(base.getTime() + offset * DAY);
}

/**
 * Resolve one lane against a season term (or fraction-only when no term). When
 * `span` is null, offsets/dates are derived from fractions × 100 (percent) so
 * the editor can still show relative sizing.
 */
export function resolveLane(
  scheme: DivisionScheme,
  seg: DivisionSegmentScheme,
  span: number | null,
  termStart: Date | null,
): ResolvedSegment[] {
  const effectiveSpan = span ?? 100;
  const bounds = cutOffsets(seg.splits, effectiveSpan);
  const out: ResolvedSegment[] = [];
  for (let i = 0; i < seg.prices.length; i++) {
    const startOffset = bounds[i] ?? 0;
    const endOffset = (bounds[i + 1] ?? effectiveSpan) - 1;
    out.push({
      scheme: seg.kind,
      index: i,
      price: seg.prices[i] ?? 0,
      startOffset,
      endOffset,
      days: endOffset - startOffset + 1,
      startDate: span != null ? addDays(termStart, startOffset) : null,
      endDate: span != null ? addDays(termStart, endOffset) : null,
    });
  }
  return out;
}

/** Resolve the whole-season ('full') segment. */
export function resolveFull(
  scheme: DivisionScheme,
  span: number | null,
  termStart: Date | null,
): ResolvedSegment {
  const effectiveSpan = span ?? 100;
  return {
    scheme: 'full',
    index: null,
    price: scheme.fullPrice,
    startOffset: 0,
    endOffset: effectiveSpan - 1,
    days: effectiveSpan,
    startDate: span != null ? addDays(termStart, 0) : null,
    endDate: span != null ? addDays(termStart, effectiveSpan - 1) : null,
  };
}

/**
 * Resolve every lane of a `DivisionScheme` against a season term into concrete
 * priced segments (used to render the editor preview + the buy flow). When the
 * term is missing, `full`/segments carry null dates but valid relative sizing.
 */
export function resolveDivisionSegments(
  scheme: DivisionScheme,
  termStart: string | Date | null | undefined,
  termEnd: string | Date | null | undefined,
): { full: ResolvedSegment; span: number | null; termStart: Date | null; schemes: Record<string, ResolvedSegment[]> } {
  const start = parseTerm(termStart);
  const span = termDaySpan(termStart, termEnd);
  const schemes: Record<string, ResolvedSegment[]> = {};
  for (const seg of scheme.schemes) {
    schemes[seg.kind] = resolveLane(scheme, seg, span, start);
  }
  return { full: resolveFull(scheme, span, start), span, termStart: start, schemes };
}

/** A membership's divisionScheme value defaulted to a safe empty shape. */
export function emptyDivisionScheme(fullPrice = 0): DivisionScheme {
  return { fullPrice, premium: 0, schemes: [] };
}

/** Coerce an unknown value (from initialData) into a DivisionScheme. */
export function coerceDivisionScheme(value: unknown, fallbackPrice = 0): DivisionScheme {
  if (value && typeof value === 'object') {
    const v = value as Partial<DivisionScheme>;
    return {
      fullPrice: typeof v.fullPrice === 'number' ? v.fullPrice : fallbackPrice,
      premium: typeof v.premium === 'number' ? v.premium : 0,
      schemes: Array.isArray(v.schemes)
        ? v.schemes.map((s) => ({
            kind: s.kind,
            splits: Array.isArray(s.splits) ? s.splits.slice() : [],
            prices: Array.isArray(s.prices) ? s.prices.slice() : [],
          }))
        : [],
    };
  }
  return emptyDivisionScheme(fallbackPrice);
}
